import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fmtCurrency, fmtDate } from '@/lib/api';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/fintech/StatusPill';
import { ArrowDownToLine, ArrowUpFromLine, ArrowRightLeft, ShieldCheck, AlertCircle } from 'lucide-react';

export default function DashboardPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);

  useEffect(() => { api.get('/wallet/overview').then(r => setOverview(r.data)); }, []);

  if (!overview) return <div className="text-sm text-[hsl(var(--muted-foreground))]">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-serif text-3xl text-[hsl(var(--brand-navy-2))]">{t('wallet.dashboard')}</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Welcome back, {user?.first_name}.</p>
      </div>

      {user?.kyc_status !== 'APPROVED' && (
        <Card className="bg-[hsl(var(--brand-gold))]/10 border-[hsl(var(--brand-gold))]/40">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-[hsl(var(--brand-navy))] flex-shrink-0" />
            <div className="text-sm flex-1">
              {user?.kyc_status === 'IN_REVIEW' ? t('wallet.kyc_pending')
                : user?.kyc_status === 'REJECTED' ? t('wallet.kyc_rejected')
                : t('wallet.kyc_required')}
            </div>
            <Button asChild size="sm" className="bg-[hsl(var(--brand-navy))]"><Link to="/wallet/kyc">{t('wallet.kyc')}</Link></Button>
          </CardContent>
        </Card>
      )}

      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif text-xl">{t('wallet.balances')}</h2>
        </div>
        <div data-testid="dashboard-balances-section" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {overview.currencies.map(c => (
            <Card key={c} data-testid={`balance-card-${c}`} className="bg-white">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold tracking-wide text-[hsl(var(--muted-foreground))]">{c}</div>
                  <div className="h-8 w-8 rounded-lg bg-[hsl(var(--brand-gold))]/10 flex items-center justify-center text-xs font-serif text-[hsl(var(--brand-navy))]">{c[0]}</div>
                </div>
                <div className="mt-3 text-2xl font-semibold tabular-nums text-[hsl(var(--brand-navy-2))]">{fmtCurrency(overview.balances[c], c)}</div>
                {overview.reserved[c] > 0 && <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">{t('wallet.reserved')}: {fmtCurrency(overview.reserved[c], c)}</div>}
                <div className="mt-4 flex gap-2">
                  <Button asChild size="sm" variant="outline" className="h-8 flex-1" data-testid={`balance-card-deposit-${c}`}><Link to={`/wallet/deposit?currency=${c}`}><ArrowDownToLine className="h-3 w-3 mr-1" />{t('wallet.deposit')}</Link></Button>
                  <Button asChild size="sm" variant="outline" className="h-8 flex-1" data-testid={`balance-card-withdraw-${c}`}><Link to={`/wallet/withdraw?currency=${c}`}><ArrowUpFromLine className="h-3 w-3 mr-1" />{t('wallet.withdraw')}</Link></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="bg-white"><CardContent className="p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-lg">{t('wallet.quick_actions')}</h3>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            <Button asChild variant="outline" className="h-auto py-4 flex flex-col gap-1"><Link to="/wallet/exchange"><ArrowRightLeft className="h-4 w-4" />{t('wallet.exchange')}</Link></Button>
            <Button asChild variant="outline" className="h-auto py-4 flex flex-col gap-1"><Link to="/wallet/deposit"><ArrowDownToLine className="h-4 w-4" />{t('wallet.deposit')}</Link></Button>
            <Button asChild variant="outline" className="h-auto py-4 flex flex-col gap-1"><Link to="/wallet/withdraw"><ArrowUpFromLine className="h-4 w-4" />{t('wallet.withdraw')}</Link></Button>
            <Button asChild variant="outline" className="h-auto py-4 flex flex-col gap-1"><Link to="/wallet/kyc"><ShieldCheck className="h-4 w-4" />{t('wallet.kyc')}</Link></Button>
          </div>
        </CardContent></Card>

        <Card className="bg-white" data-testid="dashboard-recent-transactions"><CardContent className="p-6">
          <h3 className="font-serif text-lg">{t('wallet.recent')}</h3>
          {overview.recent_transactions?.length === 0 ? (
            <div className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">{t('wallet.empty')}</div>
          ) : (
            <ul className="mt-3 divide-y">
              {overview.recent_transactions.slice(0, 6).map(tx => (
                <li key={tx.id} className="py-3 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium">{tx.type}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{fmtDate(tx.created_at)}</div>
                  </div>
                  <div className="text-right tabular-nums">
                    {tx.type === 'EXCHANGE'
                      ? <div>{fmtCurrency(tx.from_amount, tx.from_currency)} → {fmtCurrency(tx.to_amount, tx.to_currency)}</div>
                      : <div>{fmtCurrency(tx.amount, tx.currency)}</div>}
                    <StatusPill status={tx.status} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent></Card>
      </div>
    </div>
  );
}
